import {
  Stack,
  Group,
  Button,
  Table,
  Text,
  TextInput,
  Card,
  Modal,
  Alert,
  Menu,
  ActionIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconAlertCircle,
  IconCheck,
  IconPencil,
  IconTrash,
  IconPower,
  IconChevronDown,
  IconUserCircle,
} from '@tabler/icons-react';
import {
  useConductores,
  useCreateConductor,
  useUpdateConductor,
  useDeleteConductor,
  getErrorMessage,
} from '../../api/hooks';
import type { Conductor, CreateConductorDto, UpdateConductorDto } from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface FormValues {
  nombre: string;
  dni: string;
  telefono: string;
  email: string;
  direccion: string;
}

const initialFormValues: FormValues = {
  nombre: '',
  dni: '',
  telefono: '',
  email: '',
  direccion: '',
};

export function ConductoresList() {
  const { data: conductores = [], isLoading, error } = useConductores();
  const crear = useCreateConductor();
  const actualizar = useUpdateConductor();
  const eliminar = useDeleteConductor();
  const [opened, { open, close }] = useDisclosure(false);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Conductor | null>(null);

  const filtered = conductores.filter((c) =>
    [c.nombre, c.dni, c.email, c.telefono]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );

  const form = useForm<FormValues>({
    initialValues: initialFormValues,
    validate: {
      nombre: (v) => (v.trim() ? null : 'Requerido'),
    },
  });

  useEffect(() => {
    if (!opened) return;
    if (editingId) {
      const c = conductores.find((x) => x.id === editingId);
      if (c) {
        form.setValues({
          nombre: c.nombre ?? '',
          dni: c.dni ?? '',
          telefono: c.telefono ?? '',
          email: c.email ?? '',
          direccion: c.direccion ?? '',
        });
        form.resetDirty();
      }
    } else {
      form.setValues(initialFormValues);
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editingId]);

  const handleNew = () => {
    setEditingId(null);
    open();
  };

  const handleEdit = (c: Conductor) => {
    setEditingId(c.id);
    open();
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    eliminar.mutate(toDelete.id, {
      onSuccess: () => {
        notifications.show({
          title: 'Conductor eliminado',
          message: `${toDelete.nombre} ya no aparece en el catalogo.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        setToDelete(null);
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al eliminar',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  const handleToggleActivo = (c: Conductor) => {
    actualizar.mutate(
      { id: c.id, data: { activo: !c.activo } },
      {
        onSuccess: () =>
          notifications.show({
            title: c.activo ? 'Conductor desactivado' : 'Conductor reactivado',
            message: c.nombre,
            color: 'green',
            icon: <IconCheck size={18} />,
          }),
        onError: (err: unknown) =>
          notifications.show({
            title: 'Error al cambiar el estado',
            message: getErrorMessage(err),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      },
    );
  };

  const handleSubmit = (values: FormValues) => {
    const trim = (s: string): string | undefined => {
      const t = s.trim();
      return t ? t : undefined;
    };
    const basePayload = {
      nombre: values.nombre.trim(),
      dni: trim(values.dni),
      telefono: trim(values.telefono),
      email: trim(values.email),
      direccion: trim(values.direccion),
    };

    if (editingId) {
      const updateData: UpdateConductorDto = basePayload;
      actualizar.mutate(
        { id: editingId, data: updateData },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Conductor actualizado',
              message: `${values.nombre} guardado correctamente.`,
              color: 'green',
              icon: <IconCheck size={18} />,
            });
            close();
          },
          onError: (e: unknown) =>
            notifications.show({
              title: 'Error al actualizar',
              message: getErrorMessage(e),
              color: 'red',
              icon: <IconAlertCircle size={18} />,
            }),
        },
      );
      return;
    }

    const payload: CreateConductorDto = basePayload;
    crear.mutate(payload, {
      onSuccess: () => {
        notifications.show({
          title: 'Conductor dado de alta',
          message: `${values.nombre} ya está disponible.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        close();
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al dar de alta',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title="Conductores"
        subtitle="Personas autorizadas a conducir los vehículos del tenant."
        icon={IconUserCircle}
        newLabel="Nuevo conductor"
        count={conductores.length}
        onNew={handleNew}
        filters={
          <TextInput
            placeholder="Buscar por nombre, DNI o email…"
            leftSection={<IconSearch size={16} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            w={280}
          />
        }
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los conductores"
        >
          Verifica tu conexión y vuelve a intentarlo.
        </Alert>
      )}

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nombre</Table.Th>
              <Table.Th>DNI</Table.Th>
              <Table.Th>Teléfono</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th style={{ width: 90, textAlign: 'right' }}>Acciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={6}>Cargando…</Table.Td>
              </Table.Tr>
            )}
            {!isLoading && !error && filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>No hay conductores. Da de alta el primero.</Table.Td>
              </Table.Tr>
            )}
            {filtered.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>{c.nombre}</Table.Td>
                <Table.Td>
                  {c.dni ? (
                    <Text size="sm" ff="monospace">
                      {c.dni}
                    </Text>
                  ) : (
                    '—'
                  )}
                </Table.Td>
                <Table.Td>{c.telefono ?? '—'}</Table.Td>
                <Table.Td>{c.email ?? '—'}</Table.Td>
                <Table.Td>
                  <StatusBadge activo={c.activo} />
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Menu position="bottom-end" withinPortal shadow="md">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" aria-label="Acciones">
                        <IconChevronDown size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={14} />}
                        onClick={() => handleEdit(c)}
                      >
                        Editar
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconPower size={14} />}
                        onClick={() => handleToggleActivo(c)}
                      >
                        {c.activo ? 'Desactivar' : 'Reactivar'}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => setToDelete(c)}
                      >
                        Eliminar
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal
        opened={opened}
        onClose={close}
        title={editingId ? 'Editar conductor' : 'Nuevo conductor'}
        centered
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Nombre completo"
              placeholder="Juan Pérez García"
              required
              {...form.getInputProps('nombre')}
            />
            <Group grow>
              <TextInput label="DNI" placeholder="12345678A" {...form.getInputProps('dni')} />
              <TextInput
                label="Teléfono"
                placeholder="+34600111222"
                {...form.getInputProps('telefono')}
              />
            </Group>
            <TextInput
              label="Email"
              placeholder="conductor@example.com"
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Dirección"
              placeholder="Calle, ciudad"
              {...form.getInputProps('direccion')}
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={close}
                disabled={crear.isPending || actualizar.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={crear.isPending || actualizar.isPending}>
                {editingId ? 'Guardar cambios' : 'Dar de alta'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <ConfirmDialog
        opened={toDelete !== null}
        title="Eliminar conductor"
        message={toDelete ? `¿Eliminar ${toDelete.nombre}? Esta accion no se puede deshacer.` : ''}
        confirmLabel="Eliminar"
        confirmColor="red"
        loading={eliminar.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Stack>
  );
}
